import { css } from '@emotion/core';
import {
  EventDTO,
  SloGroupSummaryDTO,
  SloPerformanceDTO,
  SloQualityResultDTO,
} from '@atlassiansox/phobos-client-ts';
import { token } from '@atlaskit/tokens';

import { colors, MEET_COLOR, BREACH_COLOR } from '@/styles';

import {
  isBreach,
  isLowTrafficEvent,
  isRegularTrafficEvent,
  isSfxBreach,
  isSfxBurn,
  isSfxCtdErrorRate,
  isSfxLowTrafficAlert,
} from '../new-slo-views/common/utils';
import { GraphEventSource } from '../new-slo-views/expanded/graph/graph-tabs';

import { ERROR_BUDGET_DECIMAL_PLACES, SLI_DECIMAL_PLACES, SLO_DECIMAL_PLACES } from './constants';
import { SLOStatus } from './types';
// prettier-ignore
// eslint-disable-next-line prettier/prettier

export function truncateToDecimals(num: number, dec = 2) {
  const calcDec = Math.pow(10, dec);
  return Math.trunc(num * calcDec) / calcDec;
}

export type SLINumberProps = {
  performance?: SloPerformanceDTO;
  objective: number;
  quality?: SloQualityResultDTO;
  isGroup?: boolean;
  sloGroupSummary?: SloGroupSummaryDTO;
};

export function determineSloStatus({
  performance,
  objective,
  isGroup,
  sloGroupSummary,
}: SLINumberProps): SLOStatus {
  if (isGroup) {
    if (!sloGroupSummary) return 'neither';
    return sloGroupSummary.breachesCount === 0 ? 'meets' : 'breaches';
  }

  if (performance) {
    if (performance.result === undefined || performance.result === null) {
      return 'neither';
    }
    return performance.result * 100 >= objective ? 'meets' : 'breaches';
  }

  return 'neither';
}

export const getSloStatusColor = (status: SLOStatus): string =>
  ({
    meets: MEET_COLOR,
    breaches: BREACH_COLOR,
    neither: token('color.text.subtlest', colors.N200),
  }[status ?? 'neither']);

export const getSloErrorBudgetColor = (status: SLOStatus): string =>
  ({
    meets: 'inherit',
    breaches: BREACH_COLOR,
    neither: token('color.text.subtlest', colors.N200),
  }[status ?? 'neither']);

export const getHeroNumberColorStyles = ({ performance, objective }: SLINumberProps) => css`
  color: ${performance
    ? getSloStatusColor(determineSloStatus({ performance, objective }))
    : 'inherit'};
`;

export const getLozengeNumberAppearance = ({ performance, objective }: SLINumberProps) =>
  // eslint-disable-next-line no-nested-ternary
  performance && performance.result && performance.result > 0
    ? performance.result * 100 > objective
      ? 'success'
      : 'removed'
    : 'default';

export const getErrorBudgetNumberColorStyles = ({ performance, objective }: SLINumberProps) => css`
  color: ${performance
    ? getSloErrorBudgetColor(determineSloStatus({ performance, objective }))
    : 'inherit'};
`;

interface FormatErrorBudgetOptionsParam {
  truncate: boolean;
}
export function formatErrorBudget(
  errorBudget: number | undefined,
  options: FormatErrorBudgetOptionsParam = { truncate: true }
): string {
  if (errorBudget === undefined) {
    return 'N/A';
  }

  const { truncate } = options;

  if (truncate) {
    if (errorBudget < -999) {
      return '< -999';
    }
  }

  return truncateToDecimals(errorBudget, ERROR_BUDGET_DECIMAL_PLACES).toString();
}

export function getErrorBudget(result: number | undefined, objective: number): number | undefined {
  if (result !== undefined && result !== null && objective) {
    const errorBudgetTotal = 100 - objective;
    const errorBudget = ((result * 100 - objective) / errorBudgetTotal) * 100;

    return errorBudget;
  }

  return undefined;
}

export function formatPerformanceResult(result: number | undefined): string {
  if (result === undefined || result === null) return 'N/A';
  return truncateToDecimals(result * 100, SLI_DECIMAL_PLACES).toString();
}

export function formatObjectiveResult(result: number | undefined): string {
  if (result === undefined || result === null) return 'N/A';
  return truncateToDecimals(result, SLO_DECIMAL_PLACES).toString();
}

//  Filter a list of events into sublists by event type.
//
//  Error budget events (breach, burn and their SignalFx alert equivalents) are
//  additionally split by the lowTraffic flag. Custom detector events (CTD
//  error rate, low traffic) should fire regardless of traffic state, so they are
//  never split. See isErrorBudgetEvent / isCustomDetectorEvent in
//  new-slo-views/common/utils.
export function getEventTypes(
  events: EventDTO[],
  source: GraphEventSource = GraphEventSource.Active
) {
  const allEvents =
    (events || []).filter(event =>
      source === GraphEventSource.Historical
        ? event.isFromHistoricalIndicator
        : !event.isFromHistoricalIndicator
    ) || [];

  return {
    // Error budget events, split by low traffic state
    breachEvents: allEvents.filter(isBreach).filter(isRegularTrafficEvent),
    burnEvents: [],
    sfxBreachEvents: allEvents.filter(isSfxBreach).filter(isRegularTrafficEvent),
    sfxBurnEvents: allEvents.filter(isSfxBurn).filter(isRegularTrafficEvent),
    lowTrafficBreachEvents: allEvents.filter(isBreach).filter(isLowTrafficEvent),
    lowTrafficBurnEvents: [],
    lowTrafficSFxBreachEvents: allEvents.filter(isSfxBreach).filter(isLowTrafficEvent),
    lowTrafficSFxBurnEvents: allEvents.filter(isSfxBurn).filter(isLowTrafficEvent),
    // Custom detector events, independent of low traffic state
    sfxCtdErrorRateEvents: allEvents.filter(isSfxCtdErrorRate),
    sfxLowTrafficAlertEvents: allEvents.filter(isSfxLowTrafficAlert),
  };
}

export function getDailyPerformance(dailyGood: number, dailyBad: number) {
  return dailyGood + dailyBad > 0 ? dailyGood / (dailyGood + dailyBad) : 1.0;
}
